import { Box, Flex, Text } from "@chakra-ui/react";
import { MatchesPageSpark } from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { apiReq } from "../../lib/apiReq";
import { useStoreData } from "../../lib/store/store";
import { toaster } from "../../lib/toaster";
import {
  Avatar,
  EventRow,
  InvitePendingStatus,
  InviteSentConfirmation,
  SendInviteFooter,
} from "./InviteSparkPrimitives";
import {
  EligibleEvent,
  inviteErrorMessage,
  inviteeDisabledReason,
  useEligibleEvents,
} from "./inviteSparkShared";
import { primaryProfileImagePath } from "../../lib/profileMedia";
import { formatEventWhen } from "../../lib/formatEventWhen";

export type InviteSparkFlowProps = {
  spark: MatchesPageSpark;
  fixedEvent?: EligibleEvent;
  onDone: () => void;
  /**
   * Called after an invite is sent successfully, so the parent can refresh
   * its data (e.g. re-fetch /user/matches-page so the spark now shows its
   * pending-invite status).
   */
  onInvited?: () => void;
  /**
   * Called when the viewer picks an event they're not registered for and taps
   * "Sign up for this event" — the parent routes them to the event page to
   * grab a spot (you can only invite someone to an event you're attending).
   * When omitted, the register prompt falls back to sending (only safe when
   * every offered event is one the viewer is already registered for, e.g. the
   * event-page `fixedEvent` surface).
   */
  onRegisterForEvent?: (sparkId: number) => void;
  /**
   * Called when the picked event is the invitee's OWN upcoming event and the
   * viewer isn't registered for it — the viewer registers to JOIN them, and
   * no invite is sent (they're already going). Distinct from
   * onRegisterForEvent, which carries invite intent. Route to the event page
   * WITHOUT the invite intent.
   */
  onJoinSparkEvent?: (sparkId: number) => void;
  /**
   * Hide the avatar + name/age header. Use when the surrounding surface
   * (e.g. SparkActionsDrawer) already renders the spark's identity, so we
   * don't duplicate it. The "Invite {name} to an event" subtitle stays.
   */
  hideHeaderIdentity?: boolean;
};

export function InviteSparkFlow({
  spark,
  fixedEvent,
  onDone,
  onInvited,
  onRegisterForEvent,
  onJoinSparkEvent,
  hideHeaderIdentity,
}: InviteSparkFlowProps) {
  const firstName = spark.profile.firstName || "them";
  const storeData = useStoreData();

  const [selected, setSelected] = useState<EligibleEvent | null>(
    fixedEvent || null,
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [inviteReq] = useReq(apiReq);

  const sparkEventSparkId = spark.upcomingEvent?.sparkId;
  const viewer = useMemo(
    () => ({
      gender: storeData.user?.gender,
      age: storeData.user?.birth_date
        ? dayjs().diff(storeData.user.birth_date, "years")
        : undefined,
    }),
    [storeData.user?.gender, storeData.user?.birth_date],
  );
  const eligible = useEligibleEvents(spark, storeData.upcomingSparks, viewer);
  const sorted: EligibleEvent[] | null = fixedEvent ? [fixedEvent] : eligible;

  // On the fixedEvent surface useEligibleEvents is bypassed, so compute the
  // invitee's eligibility for the fixed event directly (age window / gender
  // capacity) from the store's full event record. When set, we warn and block
  // Send rather than letting the invite fail server-side.
  const fixedEventDisabledReason = useMemo(() => {
    if (!fixedEvent) return undefined;
    // The invitee already holds a spot at their own event — don't gate the
    // fixed-event surface on their age/capacity there (they'd show as "full"
    // precisely because they're in it). The footer handles this as a
    // "you're both going" state instead. Mirrors useEligibleEvents.
    if (spark.upcomingEvent?.sparkId === fixedEvent.sparkId) return undefined;
    const full = storeData.upcomingSparks?.find(
      (e) => e.id === fixedEvent.sparkId,
    );
    if (!full) return undefined;
    return inviteeDisabledReason(full, spark) ?? undefined;
  }, [fixedEvent, storeData.upcomingSparks, spark]);

  const handleSend = async () => {
    if (!selected) return;
    setSending(true);
    const response = await inviteReq.post("/user/spark-invitations", {
      invitedUserId: spark.profile.id,
      sparkId: selected.sparkId,
    });
    setSending(false);
    if (response.success) {
      setSent(true);
      onInvited?.();
    } else {
      toaster.create({
        type: "error",
        title: inviteErrorMessage(response.error?.code, firstName),
      });
    }
  };

  if (sent) {
    return <InviteSentConfirmation firstName={firstName} onDone={onDone} />;
  }

  // One active invite per spark. If the viewer already has one out — pending
  // or accepted to an upcoming event — (and we're not on the single-event
  // `fixedEvent` surface), show it read-only instead of the picker.
  const activeInvite = !fixedEvent ? spark.sentInvitation : undefined;

  if (activeInvite) {
    return (
      <Flex direction="column" gap="1rem">
        {!hideHeaderIdentity && (
          <Flex direction="column" align="center" gap="0.5rem" pt="0.5rem">
            <Avatar
              path={primaryProfileImagePath(spark.profile)}
              alt={firstName}
              size="72px"
            />
            <Text fontSize="lg" fontWeight="semibold">
              {firstName}
              {spark.profile.age ? `, ${spark.profile.age}` : ""}
            </Text>
          </Flex>
        )}
        <InvitePendingStatus firstName={firstName} event={activeInvite} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="1rem">
      <Flex direction="column" align="center" gap="0.5rem" pt="0.5rem">
        {!hideHeaderIdentity && (
          <>
            <Avatar
              path={primaryProfileImagePath(spark.profile)}
              alt={firstName}
              size="72px"
            />
            <Text fontSize="lg" fontWeight="semibold">
              {firstName}
              {spark.profile.age ? `, ${spark.profile.age}` : ""}
            </Text>
          </>
        )}
        <Text fontSize="sm" color="fg.muted">
          {fixedEvent
            ? `Invite ${firstName} to join you`
            : `Invite ${firstName} to an event`}
        </Text>
      </Flex>

      {fixedEvent ? (
        <Box
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          px="0.75rem"
          py="0.625rem"
        >
          <Text fontWeight="semibold" truncate>
            {fixedEvent.title || fixedEvent.activity}
          </Text>
          <Text fontSize="sm" color="fg.muted">
            {formatEventWhen(fixedEvent.startsAt)}
          </Text>
          {fixedEventDisabledReason && (
            <Text
              fontSize="xs"
              color="fg.danger"
              mt="0.5"
              fontWeight="medium"
            >
              {fixedEventDisabledReason}
            </Text>
          )}
        </Box>
      ) : !sorted ? (
        <Text color="fg.muted">Loading events…</Text>
      ) : sorted.length === 0 ? (
        <Text color="fg.muted">
          No upcoming events you're both eligible for right now.
        </Text>
      ) : (
        <Flex direction="column" gap="0.5rem">
          {sorted.map((e) => (
            <EventRow
              key={e.sparkId}
              event={e}
              sparkName={firstName}
              alreadyAttending={e.sparkId === sparkEventSparkId}
              selected={selected?.sparkId === e.sparkId}
              onPick={() => setSelected(e)}
            />
          ))}
        </Flex>
      )}

      <SendInviteFooter
        selected={selected}
        sparkEventSparkId={sparkEventSparkId}
        firstName={firstName}
        sending={sending}
        onSend={handleSend}
        onRegister={() => {
          if (selected && onRegisterForEvent) {
            onRegisterForEvent(selected.sparkId);
          } else {
            // No register handler wired (fixedEvent surface) — the viewer is
            // always registered there, so fall back to sending.
            void handleSend();
          }
        }}
        onJoin={() => {
          // The invitee is already going — register the viewer to join them,
          // with no invite attached. Only reachable on the picker surface,
          // which wires onJoinSparkEvent.
          if (selected) onJoinSparkEvent?.(selected.sparkId);
        }}
        showCopy={!fixedEvent}
        show
        disabledReason={fixedEventDisabledReason}
      />
    </Flex>
  );
}
